import EventBusyIcon from "@mui/icons-material/EventBusy";
import {
  Box,
  Chip,
  IconButton,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { CollapseChevron } from "components/CollapseChevron";
import { useState } from "react";

import { ActionsRow, DeadlinePill, HeaderRow, MissingBody } from "./ComplianceCard";
import {
  type ComplianceItem,
  type ComplianceItemMissing,
  getEscalationLabel,
  sortByDeadlineAscending,
} from "./CompliancePane.helpers";
import { COLUMN_HEADER_SX } from "./DiscrepancyReviewDialog.helpers";
import { DiscrepancyStatusChip } from "./DiscrepancyStatusChip";
import { discrepancyTableSx, EXPANDER_INSET_NO_SELECTION } from "./discrepancyTableSx";
import { getEManifestComplianceDisplay } from "./eManifestComplianceDisplay";

const COL_COUNT = 5; // chevron, Finding, Detail, Escalation, Status

const FINDING_TEXT = "No e-Manifest record in RCRAInfo";

/** Staff closed the row because the record will never arrive, not because it did. */
function isClosedAsNeverInEManifest(item: ComplianceItemMissing): boolean {
  return item.status === "resolved" && item.resolutionPath === "NEVER_IN_EMANIFEST";
}

function getDetailText(item: ComplianceItemMissing): string {
  if (isClosedAsNeverInEManifest(item)) return "Will never be in e-Manifest";
  // A resolved row has no live requirement to report — mirrors MissingBody's own guard.
  if (item.status === "resolved") return "—";
  return getEManifestComplianceDisplay({
    eManifestComplianceEscalation: item.escalation,
    exceptionReportSubmission: item.exceptionReportSubmissionId
      ? { status: item.exceptionReportSubmissionStatus }
      : null,
    transporterContactedAt: item.transporterContactedAt,
  }).tooltipText;
}

export interface MissingEManifestPaneProps {
  items: ComplianceItem[];
  now: Date;
  loading?: boolean;
  activePreviewDocId?: string | null;
  onViewDocument: (documentId: string, fileExtension: string | null) => void;
  onResolveInEManifest: (discrepancyId: string) => void;
  onAddProofOfContact: (processedManifestId: string) => void;
  onCreateExceptionReport: (processedManifestId: string) => void;
  creatingExceptionReport?: boolean;
  /** Manifest's tenant, forwarded to the expander for its doc/submission links. */
  tenantId?: string;
  /** Gates the staff-only "Will never be in e-Manifest" row action. */
  isStaff: boolean;
  onCloseNeverInEManifest: (item: ComplianceItemMissing) => void;
}

function MissingEManifestLoadingSkeleton() {
  return (
    <Stack spacing={2} sx={{ pb: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {["", "Finding", "Detail", "Escalation", "Status"].map((h, i) => (
              <TableCell key={h || `sk-${i}`}>
                <Skeleton variant="text" width={80} />
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {Array.from({ length: 2 }, (_, i) => (
            <TableRow key={i}>
              {Array.from({ length: COL_COUNT }, (__, j) => (
                <TableCell key={j}>
                  <Skeleton variant="text" width={j === 2 ? 160 : 80} />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Stack>
  );
}

function MissingEManifestHeader() {
  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox" />
        <TableCell sx={{ ...COLUMN_HEADER_SX, minWidth: 220 }}>Finding</TableCell>
        <TableCell sx={{ ...COLUMN_HEADER_SX, minWidth: 240 }}>Detail</TableCell>
        <TableCell sx={{ ...COLUMN_HEADER_SX, minWidth: 140 }}>Escalation</TableCell>
        <TableCell data-status-cell sx={{ ...COLUMN_HEADER_SX, minWidth: 100 }}>
          Status
        </TableCell>
      </TableRow>
    </TableHead>
  );
}

function ChevronCell({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  return (
    <TableCell data-icon-cell="chevron">
      <Box data-row-icon>
        <IconButton
          size="small"
          onClick={onToggle}
          aria-label={open ? "Collapse row" : "Expand row"}
        >
          <CollapseChevron collapsed={!open} />
        </IconButton>
      </Box>
    </TableCell>
  );
}

function MissingEManifestRow({
  item,
  now,
  open,
  onToggleOpen,
  activePreviewDocId,
  onViewDocument,
  onResolveInEManifest,
  onAddProofOfContact,
  onCreateExceptionReport,
  creatingExceptionReport,
  tenantId,
  isStaff,
  onCloseNeverInEManifest,
}: {
  item: ComplianceItemMissing;
  now: Date;
  open: boolean;
  onToggleOpen: () => void;
} & Pick<
  MissingEManifestPaneProps,
  | "activePreviewDocId"
  | "onViewDocument"
  | "onResolveInEManifest"
  | "onAddProofOfContact"
  | "onCreateExceptionReport"
  | "creatingExceptionReport"
  | "tenantId"
  | "isStaff"
  | "onCloseNeverInEManifest"
>) {
  const isPreviewedDoc =
    activePreviewDocId != null &&
    item.sourceDocumentId != null &&
    activePreviewDocId === item.sourceDocumentId;

  return (
    <>
      <TableRow hover data-expanded={open ? "true" : undefined}>
        <ChevronCell open={open} onToggle={onToggleOpen} />
        <TableCell sx={{ whiteSpace: "nowrap" }}>
          <Typography variant="body2">{FINDING_TEXT}</Typography>
        </TableCell>
        <TableCell sx={{ maxWidth: 320, wordBreak: "break-word" }}>
          <Typography variant="body2">{getDetailText(item)}</Typography>
        </TableCell>
        <TableCell sx={{ whiteSpace: "nowrap" }}>
          <Chip
            size="small"
            label={getEscalationLabel(item.escalation)}
            sx={{ height: 20, fontSize: "0.75rem" }}
          />
        </TableCell>
        <TableCell data-status-cell>
          <DiscrepancyStatusChip
            status={item.status}
            icon={isClosedAsNeverInEManifest(item) ? <EventBusyIcon /> : undefined}
          />
        </TableCell>
      </TableRow>
      {open && (
        <TableRow>
          {/* One column short of the row, so the deadline lands in the Status
              column it belongs under rather than at the table's right edge. */}
          <TableCell
            colSpan={COL_COUNT - 1}
            sx={{
              pt: 1.5,
              pb: 2,
              pr: 2,
              // Lines up with the row's first text column; no checkbox
              // column here, so the inset is the chevron cell alone.
              pl: EXPANDER_INSET_NO_SELECTION,
              "& > * + *": { mt: 1 },
            }}
          >
            <HeaderRow item={item} />
            <MissingBody
              item={item}
              now={now}
              onAddProofOfContact={onAddProofOfContact}
              onCreateExceptionReport={onCreateExceptionReport}
              creatingExceptionReport={creatingExceptionReport}
              tenantId={tenantId}
              isStaff={isStaff}
              onCloseNeverInEManifest={onCloseNeverInEManifest}
            />
            <Box>
              <ActionsRow
                item={item}
                isPreviewedDoc={isPreviewedDoc}
                onViewDocument={onViewDocument}
                onResolveInEManifest={onResolveInEManifest}
              />
            </Box>
          </TableCell>
          <TableCell data-status-cell sx={{ pt: 1.5, whiteSpace: "nowrap" }}>
            <DeadlinePill item={item} now={now} />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function MissingEManifestEmptyRow() {
  return (
    <TableRow>
      <TableCell colSpan={COL_COUNT} align="center" sx={{ py: 8, borderBottom: "none" }}>
        <Typography variant="body2" color="text.secondary">
          No missing e-Manifests found for the selected manifest(s).
        </Typography>
      </TableCell>
    </TableRow>
  );
}

/**
 * Missing e-Manifests as a four-column table (Finding, Detail, Escalation,
 * Status), one row per finding. Everything the old card showed lives in the
 * row's expander, opened with the leading chevron.
 */
export function MissingEManifestPane({
  items,
  now,
  loading = false,
  activePreviewDocId,
  onViewDocument,
  onResolveInEManifest,
  onAddProofOfContact,
  onCreateExceptionReport,
  creatingExceptionReport,
  tenantId,
  isStaff,
  onCloseNeverInEManifest,
}: MissingEManifestPaneProps) {
  const theme = useTheme();

  const [openIds, setOpenIds] = useState<Set<string>>(new Set());
  const toggleOpen = (id: string) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  if (loading && items.length === 0) {
    return <MissingEManifestLoadingSkeleton />;
  }

  const sortedItems = sortByDeadlineAscending(items, now);

  return (
    <TableContainer
      sx={{
        overflow: "visible",
        bgcolor: "background.paper",
        borderBottomLeftRadius: "inherit",
        borderBottomRightRadius: "inherit",
      }}
    >
      <Table
        size="small"
        sx={{
          ...discrepancyTableSx(theme),
        }}
      >
        <MissingEManifestHeader />
        <TableBody>
          {sortedItems.map((item) =>
            item.kind === "missing" ? (
              <MissingEManifestRow
                key={item.id}
                item={item}
                now={now}
                open={openIds.has(item.id)}
                onToggleOpen={() => toggleOpen(item.id)}
                activePreviewDocId={activePreviewDocId}
                onViewDocument={onViewDocument}
                onResolveInEManifest={onResolveInEManifest}
                onAddProofOfContact={onAddProofOfContact}
                onCreateExceptionReport={onCreateExceptionReport}
                creatingExceptionReport={creatingExceptionReport}
                tenantId={tenantId}
                isStaff={isStaff}
                onCloseNeverInEManifest={onCloseNeverInEManifest}
              />
            ) : null
          )}
          {sortedItems.length === 0 && <MissingEManifestEmptyRow />}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
